import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';
import JoditEditor from 'jodit-react';
import { getJoditConfig } from '../../utils/joditConfig';
import ConfirmModal from '../../components/ConfirmModal';

const AddCountry = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showBackModal, setShowBackModal] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [faqs, setFaqs] = useState([{ question: '', answer: '' }]);
  const [recentFaqs, setRecentFaqs] = useState([{ question: '', answer: '' }]);
  const [keywordLinks, setKeywordLinks] = useState([{ keyword: '', link: '' }]);

  const [formData, setFormData] = useState({
    name: '',
    currencyId: '',
    domestic: '',
    international: '',
    latitude: '',
    longitude: '',
    about: '',
    metaTitle: '',
    metaScript: '',
    metaKeyword: '',
    metaDescription: '',
    author: ''
  });

  const editorConfig = useMemo(() => getJoditConfig(), []);

  useEffect(() => {
    fetchCurrencies();

    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
      setShowBackModal(true);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const fetchCurrencies = async () => {
    try {
      const response = await apiService.get('countryAdd');
      setCurrencies(response.data || []);
    } catch (error) {
      console.error('Error fetching currencies:', error);
      toast.error('Failed to load currencies');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAboutChange = (value) => {
    setFormData(prev => ({ ...prev, about: value }));
  };

  const handleImageChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be 5MB or less');
        e.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'image') setImagePreview(reader.result);
        else setIconPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const addFaq = () => {
    setFaqs([...faqs, { question: '', answer: '' }]);
  };

  const removeFaq = (index) => {
    setFaqs(faqs.filter((_, i) => i !== index));
  };

  const updateFaq = (index, field, value) => {
    const updated = [...faqs];
    updated[index][field] = value;
    setFaqs(updated);
  };

  const addRecentFaq = () => {
    setRecentFaqs([...recentFaqs, { question: '', answer: '' }]);
  };

  const removeRecentFaq = (index) => {
    setRecentFaqs(recentFaqs.filter((_, i) => i !== index));
  };

  const updateRecentFaq = (index, field, value) => {
    const updated = [...recentFaqs];
    updated[index][field] = value;
    setRecentFaqs(updated);
  };

  const addKeywordLink = () => {
    setKeywordLinks([...keywordLinks, { keyword: '', link: '' }]);
  };

  const removeKeywordLink = (index) => {
    setKeywordLinks(keywordLinks.filter((_, i) => i !== index));
  };

  const updateKeywordLink = (index, field, value) => {
    const updated = [...keywordLinks];
    updated[index][field] = value;
    setKeywordLinks(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Country name is required');
      return;
    }
    if (!formData.currencyId) {
      toast.error('Please select a currency');
      return;
    }

    setLoading(true);
    const submitData = new FormData();

    Object.keys(formData).forEach(key => {
      if (formData[key]) submitData.append(key, formData[key]);
    });

    const imageFile = document.querySelector('input[name="image"]').files[0];
    const iconFile = document.querySelector('input[name="icon"]').files[0];
    if (imageFile) submitData.append('image', imageFile);
    if (iconFile) submitData.append('icon', iconFile);

    faqs.forEach((faq, index) => {
      if (faq.question.trim() || faq.answer.trim()) {
        submitData.append(`faq[${index}][question]`, faq.question);
        submitData.append(`faq[${index}][answer]`, faq.answer);
      }
    });

    recentFaqs.forEach((faq, index) => {
      if (faq.question.trim() || faq.answer.trim()) {
        submitData.append(`recentFaq[${index}][question]`, faq.question);
        submitData.append(`recentFaq[${index}][answer]`, faq.answer);
      }
    });

    keywordLinks.forEach((kl, index) => {
      if (kl.keyword.trim() || kl.link.trim()) {
        submitData.append(`keywordLink[${index}][keyword]`, kl.keyword);
        submitData.append(`keywordLink[${index}][link]`, kl.link);
      }
    });

    try {
      const response = await apiService.postFormData('updateCountry', submitData);
      if (!response.status) {
        toast.success('Country added successfully');
        setTimeout(() => navigate('/country'), 1000);
      } else {
        toast.error(response.message || 'An error occurred');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-0 sm:p-0">
      {/* Breadcrumb */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Add Country</h1>
        <nav className="text-xs sm:text-sm flex flex-wrap items-center">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-500">Location</span>
          <span className="mx-2 text-gray-400">/</span>
          <Link to="/country" className="text-gray-500 hover:text-gray-700">Country</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Add Country</span>
        </nav>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Main Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-6">
            {/* Image Upload */}
            <div className="md:col-span-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Image</label>
              <div className="relative w-full max-w-xs h-48 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50 mx-auto md:mx-0">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                <input type="file" name="image" accept="image/*" onChange={(e) => handleImageChange(e, 'image')} className="absolute inset-0 opacity-0 cursor-pointer" />
              </div>
            </div>

            {/* Icon Upload */}
            <div className="md:col-span-3">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Icon</label>
              <div className="relative w-full max-w-[144px] h-36 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50 mx-auto md:mx-0">
                {iconPreview ? (
                  <img
                    src={iconPreview}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                <input type="file" name="icon" accept="image/*" onChange={(e) => handleImageChange(e, 'icon')} className="absolute inset-0 opacity-0 cursor-pointer" />
              </div>
            </div>

            {/* Country Name */}
            <div className="md:col-span-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Country Name <span className="text-red-500">*</span></label>
              <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Enter country name" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" required />
            </div>
          </div>

          {/* Currency and Prices */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Select Currency <span className="text-red-500">*</span></label>
              <select name="currencyId" value={formData.currencyId} onChange={handleInputChange} className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" required>
                <option value="">Select Currency</option>
                {currencies.map(curr => (
                  <option key={curr._id} value={curr._id}>{curr.name} ({curr.currencyName})</option>
                ))}
              </select>
            </div>
            {user?.role === 'admin' && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Domestic Price</label>
                  <input type="number" name="domestic" value={formData.domestic} onChange={handleInputChange} placeholder="Price" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">International Price</label>
                  <input type="number" name="international" value={formData.international} onChange={handleInputChange} placeholder="Price" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
              </>
            )}
          </div>

          {/* Coordinates and Author */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Latitude</label>
              <input type="number" step="any" name="latitude" value={formData.latitude} onChange={handleInputChange} placeholder="Enter latitude" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Longitude</label>
              <input type="number" step="any" name="longitude" value={formData.longitude} onChange={handleInputChange} placeholder="Enter longitude" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Author</label>
              <input type="text" name="author" value={formData.author} onChange={handleInputChange} placeholder="Enter author name" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
            </div>
          </div>

          {/* About */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">About</label>
            <JoditEditor
              value={formData.about}
              config={editorConfig}
              onBlur={newContent => setFormData(prev => ({ ...prev, about: newContent }))}
            />
          </div>

          {/* FAQ Section */}
          <div className="mb-6">
            <label className="block text-lg font-bold text-gray-900 mb-4">FAQ Section</label>
            {faqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 mb-3">
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Question</label>
                  <input type="text" value={faq.question} onChange={(e) => updateFaq(index, 'question', e.target.value)} placeholder="Enter FAQ question" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Answer</label>
                  <textarea value={faq.answer} onChange={(e) => updateFaq(index, 'answer', e.target.value)} placeholder="Enter FAQ answer" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"></textarea>
                </div>
                <button type="button" onClick={() => removeFaq(index)} className="px-3 py-1 border border-red-600 text-red-600 text-sm rounded-lg hover:bg-red-50">Remove FAQ</button>
              </div>
            ))}
            <button type="button" onClick={addFaq} className="px-2 sm:px-4 py-2 border border-[#009ef7] text-[#009ef7] text-sm rounded-lg hover:bg-[#009ef7]/10">Add FAQ</button>
          </div>

          {/* Recent FAQ Section */}
          <div className="mb-6">
            <label className="block text-lg font-bold text-gray-900 mb-4">Recent FAQ Section</label>
            {recentFaqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 mb-3">
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Question</label>
                  <input type="text" value={faq.question} onChange={(e) => updateRecentFaq(index, 'question', e.target.value)} placeholder="Enter Recent FAQ question" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Answer</label>
                  <textarea value={faq.answer} onChange={(e) => updateRecentFaq(index, 'answer', e.target.value)} placeholder="Enter Recent FAQ answer" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"></textarea>
                </div>
                <button type="button" onClick={() => removeRecentFaq(index)} className="px-3 py-1 border border-red-600 text-red-600 text-sm rounded-lg hover:bg-red-50">Remove Recent FAQ</button>
              </div>
            ))}
            <button type="button" onClick={addRecentFaq} className="px-2 sm:px-4 py-2 border border-[#009ef7] text-[#009ef7] text-sm rounded-lg hover:bg-[#009ef7]/10">Add Recent FAQ</button>
          </div>

          {/* Keyword Link Section */}
          <div className="mb-6">
            <label className="block text-lg font-bold text-gray-900 mb-4">Keyword Link Section</label>
            {keywordLinks.map((kl, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 mb-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Keyword</label>
                    <input type="text" value={kl.keyword} onChange={(e) => updateKeywordLink(index, 'keyword', e.target.value)} placeholder="Enter keyword" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Link</label>
                    <input type="url" value={kl.link} onChange={(e) => updateKeywordLink(index, 'link', e.target.value)} placeholder="Enter link URL" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                  </div>
                </div>
                <button type="button" onClick={() => removeKeywordLink(index)} className="px-3 py-1 border border-red-600 text-red-600 text-sm rounded-lg hover:bg-red-50">Remove</button>
              </div>
            ))}
            <button type="button" onClick={addKeywordLink} className="px-2 sm:px-4 py-2 border border-[#009ef7] text-[#009ef7] text-sm rounded-lg hover:bg-[#009ef7]/10">Add Keyword Link</button>
          </div>
        </div>

        {/* SEO Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">SEO</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Meta Title</label>
              <input type="text" name="metaTitle" value={formData.metaTitle} onChange={handleInputChange} placeholder="Enter meta title" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Meta Script (Schema)</label>
              <textarea name="metaScript" value={formData.metaScript} onChange={handleInputChange} rows="3" placeholder="Type your text here..." className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"></textarea>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Meta Keywords</label>
              <textarea name="metaKeyword" value={formData.metaKeyword} onChange={handleInputChange} rows="3" placeholder="Enter meta keywords" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"></textarea>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Meta Description</label>
              <textarea name="metaDescription" value={formData.metaDescription} onChange={handleInputChange} rows="3" placeholder="Enter meta Description" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"></textarea>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-end gap-3">
          <button type="button" onClick={() => setShowBackModal(true)} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-center">Cancel</button>
          <button type="submit" disabled={loading} className="px-6 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#009ef7]/90 disabled:opacity-50">
            {loading ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </form>

      <ConfirmModal
        isOpen={showBackModal}
        title="Are you sure you want to go back?"
        message="Any unsaved country details will be lost."
        confirmText="Yes, Go Back"
        cancelText="Stay Here"
        onConfirm={() => navigate('/country')}
        onCancel={() => setShowBackModal(false)}
      />
    </div>
  );
};

export default AddCountry;
